import { Box, Spinner } from '@chakra-ui/react'
import { useProductQuery } from '@stocker/codegen/vendure'
import { useProductSeoQuery } from '@stocker/codegen/pimcore'
import { ProductPageResponsive } from '@stocker/ui-components/custom'
import { Seo } from '@stocker/ui-components/design-system'
import { getServerData, htmlToMetaDescription, mergePimcoreSeo, resolveSeo } from '@stocker/ui-components/helpers'
import { QueryClient, dehydrate } from '@tanstack/react-query'
import type { GetStaticPaths, GetStaticProps, InferGetStaticPropsType, NextPage } from 'next'
import { useRouter } from 'next/router'
import type { ParsedUrlQuery } from 'querystring'

interface IStaticParams extends ParsedUrlQuery {
  slug: string
}

export const getStaticPaths: GetStaticPaths<IStaticParams> = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getStaticProps: GetStaticProps = async (context) => {
  const { slug } = context.params as IStaticParams
  const language = context.locale ?? 'de'
  const queryClient = new QueryClient()

  try {
    const data = await useProductQuery.fetcher({
      slug,
      withCustomFields: true,
      isPreRender: true,
    })()

    if (!data.product) {
      console.log(`[Product detail page] Product not found for slug: ${slug}`)

      return { notFound: true }
    }

    queryClient.setQueryData(
      useProductQuery.getKey({
        slug,
        withCustomFields: true,
        isPreRender: true,
      }),
      data,
    )

    // Per-product SEO from Pimcore, keyed by the product's pimcoreId (master). Prefetched here so
    // the meta tags are server-rendered into the HTML (crawlers/social scrapers run no JS).
    // A Pimcore miss/error must never break the page — the component falls back to Vendure data.
    const pimcoreId = data.product.customFields?.pimcoreId
    if (pimcoreId != null) {
      try {
        const seo = await useProductSeoQuery.fetcher({ id: pimcoreId, language })()
        queryClient.setQueryData(useProductSeoQuery.getKey({ id: pimcoreId, language }), seo)
      } catch (error) {
        console.warn(`[Product detail page] Pimcore SEO fetch failed for pimcoreId ${pimcoreId}: ${error}`)
      }
    }
  } catch (error) {
    // return handlePrefetchError(error)
    console.log(`[Product detail page] Error fetching product: ${error}`)

    return {
      notFound: true,
      revalidate: 1,
    }
  }

  const serverData = await getServerData()

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      revalidateTime: Date.now(),
      ...serverData, // Data needed for _app.tsx Header, Footer etc
    },
    revalidate: 600,
  }
}

const ProductPage: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = () => {
  const router = useRouter()
  const { slug } = router.query
  const language = router.locale ?? 'de'
  const { data, error, isLoading } = useProductQuery(
    { slug: slug as string, withCustomFields: true, isPreRender: true },
    { staleTime: 1000 * 60 * 10 },
  )

  // SEO cascade: variant → master → Vendure/default. The master is prefetched server-side (SSR
  // baseline crawlers/scrapers see). The active variant comes from `?sku` — known only client-side
  // under SSG — so the variant layer refines the head after mount (browser + Google's JS wave).
  const masterId = data?.product?.customFields?.pimcoreId ?? undefined
  const activeSku = typeof router.query.sku === 'string' ? router.query.sku : undefined
  const variantId = activeSku
    ? data?.product?.variants?.find(v => v.sku === activeSku)?.customFields?.pimcoreId ?? undefined
    : undefined

  const { data: masterSeo } = useProductSeoQuery(
    { id: masterId as number, language },
    { enabled: masterId != null, staleTime: 1000 * 60 * 10 },
  )
  const { data: variantSeo } = useProductSeoQuery(
    { id: variantId as number, language },
    { enabled: variantId != null && variantId !== masterId, staleTime: 1000 * 60 * 10 },
  )

  // TODO: [AC-357] Actual loading state
  if (isLoading) {
    return <Spinner />
  }

  // TODO: Actual error state
  if (error) {
    return <Box>An Error has Occured...</Box>
  }

  if (data?.product) {
    // Pimcore SEO wins (variant over master); fall back to Vendure data. `_` is stripped from the
    // name (red formatting).
    // `seoNoIndex` + `seoCanonical` are indexing controls that MUST match the SSR HTML (crawlers and
    // social scrapers run no JS). The variant layer is client-only, so those two always come from
    // the master (SSR) layer — only title/description/keywords/og:image get the variant refinement.
    const master = masterSeo?.productSeo
    const merged = {
      ...mergePimcoreSeo(variantSeo?.productSeo, master),
      seoNoIndex: master?.seoNoIndex ?? null,
      seoCanonical: master?.seoCanonical ?? null,
    }
    // Vendure `description` is empty for these products — the descriptive block (Pimcore
    // `w2pBlockOutro`) is synced into the `infoText` custom field. Cascade variant → master to
    // mirror the Pimcore SEO cascade.
    const activeVariant = activeSku ? data.product.variants?.find(v => v.sku === activeSku) : undefined
    const seo = resolveSeo(merged, {
      title: (
        activeVariant?.customFields?.productDetailName ||
        data.product.customFields?.productDetailName ||
        data.product.name
      ).replaceAll('_', ''),
      description: htmlToMetaDescription(
        activeVariant?.customFields?.infoText || data.product.customFields?.infoText,
      ),
      ogImage: activeVariant?.assets?.[0]?.source || data.product.assets?.[0]?.source,
    })

    return (
      <>
        <Seo
          title={seo.title}
          description={seo.description}
          keywords={seo.keywords}
          ogImage={seo.ogImage}
          noIndex={seo.noIndex}
          canonicals={seo.canonicals}
          titleIsComplete={seo.titleIsComplete}
          ogType="product"
        />
        <ProductPageResponsive product={data.product} />
      </>
    )
  }

  return <Box>Something went wrong...</Box>
}

export default ProductPage
